/**
 * The configurable big screen's wire (GH-927, from the GH-924 proposal). The names here are fixed:
 * the live display and the console both read them exactly.
 *
 * Public: `display.layout` on `GET /v1/public/events/:eventId/presentation` carries only the
 * RESOLVED arrangement -- which item is big and which (up to three) take slots -- as bare
 * `{ kind, promptId }` pairs. No settings internals, no waiting list, no row ids.
 *
 * Staff: `GET /v1/events/:eventId/screen-items` and `PATCH .../screen-items/:itemId` carry the
 * configured list with each item's resolved `placement`.
 */
import { z } from "zod";
export const SCREEN_ITEM_KINDS = [
    "prompt",
    "trivia",
    "raffle_countdown",
    "recent_gifts",
    "appeal",
];
export const screenItemKindSchema = z.enum(SCREEN_ITEM_KINDS);
export const SCREEN_ITEM_STATES = ["show", "big", "hide"];
export const screenItemStateSchema = z.enum(SCREEN_ITEM_STATES);
/** At most this many 'show' items take a slot at once (GH-924 rule 3). */
export const SCREEN_SLOT_LIMIT = 3;
/** One resolved item on the public wire. */
export const screenItemSchema = z.object({
    kind: screenItemKindSchema,
    promptId: z.string().uuid().nullable(),
});
export const screenLayoutSchema = z.object({
    big: screenItemSchema.nullable(),
    slots: z.array(screenItemSchema).max(SCREEN_SLOT_LIMIT),
});
export const screenItemPlacementSchema = z.enum(["big", "slot", "waiting", "off"]);
export const staffScreenItemSchema = z.object({
    id: z.string().uuid(),
    kind: screenItemKindSchema,
    promptId: z.string().uuid().nullable(),
    label: z.string(),
    state: screenItemStateSchema,
    order: z.number().int(),
    isLive: z.boolean(),
    placement: screenItemPlacementSchema,
});
export const staffScreenItemsResponseSchema = z.object({
    items: z.array(staffScreenItemSchema),
});
/** `PATCH /v1/events/:eventId/screen-items/:itemId`. At least one field. */
export const screenItemPatchSchema = z
    .object({
    state: screenItemStateSchema.optional(),
    order: z.number().int().min(0).max(10_000).optional(),
})
    .strict()
    .refine((value) => value.state !== undefined || value.order !== undefined, {
    message: "Send a state, an order, or both.",
});
//# sourceMappingURL=screen-layout.js.map